import { useRef, useState } from 'react';
import toast from 'react-hot-toast';

import ClientFarmSearch from '../../clients/forms/search';
import UserSelect from '../../users/forms/search';
import { IconClose, IconPlus, IconTrash } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';

import './styles/session-modal.css';

// Step 1 of the OPU flow — floating form to open a session:
//   · farm        (required) — picked via the combined client/farm typeahead
//   · staff       (optional) — users with a fixed role: Aspirador / Asistente
//   · date        (optional) — only for back-dating a past session; empty = today
//
// Role labels map to contract values (aspiration_session_staff.role):
//   Aspirador → lead_vet · Asistente → assistant
const ROLE_OPTIONS = [
    { value: 'lead_vet',  label: 'Aspirador' },
    { value: 'assistant', label: 'Asistente' },
];
const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map(r => [r.value, r.label]));

// local 'YYYY-MM-DD' (en-CA renders ISO) — caps the picker at today.
const TODAY = new Date().toLocaleDateString('en-CA');

export default function SessionModal({ onConfirm, onCancel }) {
    const cardRef = useModalDialog(onCancel);
    const [farm, setFarm] = useState(null);
    const [staff, setStaff] = useState([]);
    const [staffUser, setStaffUser] = useState(null);
    const [staffRole, setStaffRole] = useState('lead_vet');
    const [userKey, setUserKey] = useState(0); // bump to reset the user typeahead after add
    const [date, setDate] = useState('');
    const seq = useRef(0);

    const pickFarm = (item) => setFarm({ id: item.id, name: item.name, owner: item.owner || null });

    const addStaff = () => {
        if (!staffUser) { toast.error('Elegí un usuario'); return; }
        if (staff.some(s => s.user.id === staffUser.id && s.role === staffRole)) {
            toast.error('Ese usuario ya tiene ese rol en la sesión');
            return;
        }
        setStaff(prev => [...prev, { key: `s${++seq.current}`, user: staffUser, role: staffRole }]);
        setStaffUser(null);
        setUserKey(k => k + 1);
    };

    const removeStaff = (key) => setStaff(prev => prev.filter(s => s.key !== key));

    const handleContinue = () => {
        if (!farm) { toast.error('La finca es obligatoria'); return; }
        onConfirm({ farm, staff, date: date || null });
    };

    return (
        <div className="opu-modal">
            <div className="opu-modal__overlay" onClick={onCancel} aria-hidden="true" />
            <div className="opu-modal__card" role="dialog" aria-modal="true" aria-label="Nueva sesión de OPU" ref={cardRef}>
                <header className="opu-modal__head">
                    <h2 className="opu-modal__title">Nueva sesión de OPU</h2>
                    <button type="button" className="opu-modal__close" onClick={onCancel} aria-label="Cerrar">
                        <IconClose />
                    </button>
                </header>

                <div className="opu-modal__body">
                    {/* ---- finca ---- */}
                    <div className="opu-field">
                        <span className="opu-field__label">Finca de la aspiración</span>
                        {farm ? (
                            <div className="opu-chip">
                                <span className="opu-chip__text">
                                    {farm.name}{farm.owner?.name ? ` · ${farm.owner.name}` : ''}
                                </span>
                                <button type="button" className="opu-chip__clear" onClick={() => setFarm(null)} aria-label="Cambiar finca">
                                    <IconClose />
                                </button>
                            </div>
                        ) : (
                            <ClientFarmSearch farmsOnly onSelect={pickFarm} placeholder="Buscar finca o dueño…" autoFocus />
                        )}
                    </div>

                    {/* ---- staff ---- */}
                    <div className="opu-field">
                        <span className="opu-field__label">Equipo (opcional)</span>
                        <div className="opu-staff__add">
                            <UserSelect key={userKey} onSelect={setStaffUser} placeholder="Buscar usuario…" />
                            <select value={staffRole} onChange={(e) => setStaffRole(e.target.value)}>
                                {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                            <button type="button" className="opu-staff__add-btn" onClick={addStaff}>
                                <IconPlus /><span>Agregar</span>
                            </button>
                        </div>

                        {staff.length > 0 && (
                            <ul className="opu-staff__list">
                                {staff.map(s => (
                                    <li key={s.key} className="opu-staff__item">
                                        <span className="opu-staff__name">{s.user.name}</span>
                                        <span className="opu-staff__role">{ROLE_LABEL[s.role]}</span>
                                        <button type="button" className="opu-staff__remove" onClick={() => removeStaff(s.key)} aria-label="Quitar">
                                            <IconTrash />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* ---- fecha ---- */}
                    <div className="opu-field">
                        <span className="opu-field__label">Fecha de la jornada (opcional)</span>
                        <input
                            type="date"
                            className="opu-field__date"
                            value={date}
                            max={TODAY}
                            onChange={(e) => setDate(e.target.value)}
                        />
                        <span className="opu-field__hint">Dejala vacía para hoy. Usá una fecha solo si registrás una jornada pasada.</span>
                    </div>
                </div>

                <footer className="opu-modal__foot">
                    <button type="button" className="opu-modal__cancel" onClick={onCancel}>Cancelar</button>
                    <button type="button" className="opu-modal__continue" onClick={handleContinue}>Continuar</button>
                </footer>
            </div>
        </div>
    );
}
